import { useEffect, useState, useMemo } from "react"
import { Link, useSearchParams, useNavigate } from "react-router-dom"
import { SuperAdminLayout } from "@/components/superadmin/SuperAdminLayout"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import {
  Search, Plus, Pencil, Trash2, Eye, Calendar, Users,
  CheckCircle2, X, ClipboardList, Video, Building2, Briefcase, MoreHorizontal
} from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { DataTable, EmptyState, IconTile, Page, PageHeader, Pagination, Panel, SearchInput, Segmented, StatCard, StatGrid, Tag, Toolbar, rowClass, td, th, usePagination, type Tone } from "@/components/superadmin/kit"
import { cn } from "@/lib/utils"
import type { ReuniaoComercial, TipoReuniao, StatusReuniao, ParticipanteReuniao, ItemPauta, Ata } from "@/types/comercial"

// ── Helpers ──────────────────────────────────────────────────────────────────
function uid() { return crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) }
function fmtDate(d: string) { return d ? new Date(d).toLocaleDateString("pt-BR") : "—" }
function fmtHora(h: string) { return h || "—" }

const TIPO_LABELS: Record<TipoReuniao, string> = {
  interna: "Interna",
  cliente: "Com cliente",
  tecnica: "Técnica",
  diretoria: "Diretoria",
}
const TIPO_ICONS: Record<TipoReuniao, any> = {
  interna: Users,
  cliente: Building2,
  tecnica: Briefcase,
  diretoria: Video,
}
const STATUS_LABELS: Record<StatusReuniao, string> = {
  agendada: "Agendada",
  realizada: "Realizada",
  cancelada: "Cancelada",
}
const STATUS_TONE: Record<StatusReuniao, Tone> = {
  agendada: "accent",
  realizada: "success",
  cancelada: "neutral",
}

function emptyReuniao(): ReuniaoComercial {
  return {
    id: uid(),
    titulo: "",
    tipo: "interna",
    status: "agendada",
    data: new Date().toISOString().split("T")[0],
    horaInicio: "09:00",
    horaFim: "10:00",
    local: "",
    linkVideo: "",
    participantes: [],
    pauta: [],
    observacoes: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

// ── ATA editor modal ──────────────────────────────────────────────────────────

const AtaModal = ({
  open, reuniao, onClose, onSave,
}: {
  open: boolean
  reuniao: ReuniaoComercial | null
  onClose: () => void
  onSave: (r: ReuniaoComercial) => void
}) => {
  const { toast } = useToast()
  const [conteudo, setConteudo] = useState("")
  const [decisoes, setDecisoes] = useState("")
  const [acoes, setAcoes] = useState<ItemPauta[]>([])

  useMemo(() => {
    if (open && reuniao) {
      setConteudo(reuniao.ata?.conteudo ?? "")
      setDecisoes(reuniao.ata?.decisoes ?? "")
      setAcoes(reuniao.ata?.acoes ?? [])
    }
  }, [open, reuniao])

  const addAcao = () => setAcoes(prev => [
    ...prev,
    { id: uid(), ordem: prev.length + 1, titulo: "", responsavel: "", prazo: "", status: "pendente" }
  ])

  const updateAcao = (idx: number, k: keyof ItemPauta, v: any) =>
    setAcoes(prev => { const a = [...prev]; a[idx] = { ...a[idx], [k]: v }; return a })

  const removeAcao = (idx: number) => setAcoes(prev => prev.filter((_, i) => i !== idx))

  const handleSave = () => {
    if (!reuniao) return
    const now = new Date().toISOString()
    const versao = { id: uid(), conteudo, editadoPor: "Super Admin", editadoEm: now }
    const ata: Ata = {
      id: reuniao.ata?.id ?? uid(),
      reuniaoId: reuniao.id,
      conteudo,
      decisoes,
      acoes,
      historico: [...(reuniao.ata?.historico ?? []), versao],
      createdAt: reuniao.ata?.createdAt ?? now,
      updatedAt: now,
    }
    onSave({ ...reuniao, ata, status: "realizada", updatedAt: now })
    toast({ title: "Ata salva", description: "Ata registrada com sucesso." })
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>📋 Ata da Reunião — {reuniao?.titulo}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Transcrição / Resumo da Reunião</Label>
            <Textarea
              value={conteudo}
              onChange={e => setConteudo(e.target.value)}
              placeholder="Descreva o que foi discutido na reunião. Pode ser uma transcrição livre ou um resumo dos pontos abordados..."
              className="mt-1 min-h-[120px]"
            />
          </div>
          <div>
            <Label>Decisões Tomadas</Label>
            <Textarea
              value={decisoes}
              onChange={e => setDecisoes(e.target.value)}
              placeholder="Liste as decisões formais tomadas durante a reunião..."
              className="mt-1 min-h-[80px]"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Ações e Responsáveis</Label>
              <Button size="sm" variant="outline" onClick={addAcao} className="gap-1 h-7 text-xs">
                <Plus className="h-3 w-3" />
                Adicionar ação
              </Button>
            </div>
            {acoes.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4 border-2 border-dashed border-gray-200 rounded-lg">
                Nenhuma ação adicionada ainda
              </p>
            ) : (
              <div className="space-y-2">
                {acoes.map((a, idx) => (
                  <div key={a.id} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      <Input value={a.titulo} onChange={e => updateAcao(idx, "titulo", e.target.value)} placeholder="Ação / tarefa" className="text-sm" />
                    </div>
                    <div className="col-span-3">
                      <Input value={a.responsavel ?? ""} onChange={e => updateAcao(idx, "responsavel", e.target.value)} placeholder="Responsável" className="text-sm" />
                    </div>
                    <div className="col-span-3">
                      <Input type="date" value={a.prazo ?? ""} onChange={e => updateAcao(idx, "prazo", e.target.value)} className="text-sm" />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => removeAcao(idx)}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <ClipboardList className="h-4 w-4" />
            Salvar Ata
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Component Principal ───────────────────────────────────────────────────────

const ReunioesComercias = () => {
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const [reunioes, setReunioes] = useState<ReuniaoComercial[]>([])
  const [search, setSearch] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterTipo, setFilterTipo] = useState("all")
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const [editorOpen, setEditorOpen] = useState(searchParams.get("action") === "new")
  const [editing, setEditing] = useState<ReuniaoComercial | null>(null)
  const [form, setForm] = useState<ReuniaoComercial>(emptyReuniao())
  const [novoParticipante, setNovoParticipante] = useState("")

  const [ataOpen, setAtaOpen] = useState(false)
  const [reuniaoAta, setReuniaAta] = useState<ReuniaoComercial | null>(null)

  const filtered = useMemo(() =>
    reunioes.filter(r => {
      const q = search.toLowerCase()
      return (r.titulo.toLowerCase().includes(q) || r.participantes.some(p => p.nome.toLowerCase().includes(q))) &&
        (filterStatus === "all" || r.status === filterStatus) &&
        (filterTipo === "all" || r.tipo === filterTipo)
    }), [reunioes, search, filterStatus, filterTipo])

  const pager = usePagination(filtered, 12)
  useEffect(() => pager.reset(), [search, filterStatus, filterTipo]) // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => ({
    total: reunioes.length,
    agendadas: reunioes.filter(r => r.status === "agendada").length,
    realizadas: reunioes.filter(r => r.status === "realizada").length,
    comAta: reunioes.filter(r => !!r.ata).length,
  }), [reunioes])

  const setField = (k: keyof ReuniaoComercial, v: any) => setForm(p => ({ ...p, [k]: v }))

  const openNew = () => { setEditing(null); setForm(emptyReuniao()); setEditorOpen(true) }
  const openEdit = (r: ReuniaoComercial) => { setEditing(r); setForm({ ...r }); setEditorOpen(true) }

  const addParticipante = () => {
    if (!novoParticipante.trim()) return
    const p: ParticipanteReuniao = { id: uid(), nome: novoParticipante.trim(), tipo: "interno" }
    setForm(prev => ({ ...prev, participantes: [...prev.participantes, p] }))
    setNovoParticipante("")
  }

  const removeParticipante = (id: string) => setForm(prev => ({ ...prev, participantes: prev.participantes.filter(p => p.id !== id) }))

  const handleSave = () => {
    if (!form.titulo.trim()) { toast({ title: "Erro", description: "Título é obrigatório", variant: "destructive" }); return }
    const updated = { ...form, updatedAt: new Date().toISOString() }
    setReunioes(prev => {
      const idx = prev.findIndex(r => r.id === updated.id)
      if (idx >= 0) { const arr = [...prev]; arr[idx] = updated; return arr }
      return [...prev, updated]
    })
    setEditorOpen(false)
    toast({ title: editing ? "Reunião atualizada" : "Reunião criada" })
  }

  const handleDelete = (id: string) => {
    setReunioes(prev => prev.filter(r => r.id !== id))
    toast({ title: "Reunião excluída" })
  }

  const handleSaveAta = (r: ReuniaoComercial) => {
    setReunioes(prev => prev.map(x => x.id === r.id ? r : x))
    setAtaOpen(false)
    setReuniaAta(null)
  }

  const openAta = (r: ReuniaoComercial) => { setReuniaAta(r); setAtaOpen(true) }

  return (
    <SuperAdminLayout>
      <Page>
        <PageHeader
          title="Reuniões e atas"
          description="Agenda comercial com pauta, participantes e ata de cada reunião."
          actions={<Button className="h-9 gap-1.5" onClick={openNew}><Plus className="h-4 w-4" />Nova reunião</Button>}
        />

        <StatGrid>
          <StatCard label="Agendadas" value={stats.agendadas} hint="Próximos compromissos" icon={Calendar} tone="accent" />
          <StatCard label="Realizadas" value={stats.realizadas} hint={`${stats.total} no total`} icon={CheckCircle2} tone="success" />
          <StatCard label="Com ata" value={stats.comAta} hint={stats.realizadas - stats.comAta > 0 ? `${stats.realizadas - stats.comAta} realizada(s) sem ata` : "Todas documentadas"} hintTone={stats.realizadas - stats.comAta > 0 ? "warning" : undefined} icon={ClipboardList} />
          <StatCard label="Participantes" value={reunioes.reduce((t, r) => t + r.participantes.length, 0)} hint="Somando todas as reuniões" icon={Users} />
        </StatGrid>

        <Panel flush>
          <Toolbar>
            <Segmented
              value={filterStatus}
              onChange={setFilterStatus}
              options={[
                { value: "all", label: "Todas", count: reunioes.length },
                ...(Object.keys(STATUS_LABELS) as StatusReuniao[]).map(s => ({ value: s, label: STATUS_LABELS[s], count: reunioes.filter(r => r.status === s).length })),
              ]}
            />
            <div className="flex flex-col gap-2 sm:flex-row lg:ml-auto">
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger className="h-8 w-full text-[13px] sm:w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {(Object.keys(TIPO_LABELS) as TipoReuniao[]).map(t => <SelectItem key={t} value={t}>{TIPO_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
              <SearchInput value={search} onChange={setSearch} placeholder="Reunião ou participante" />
            </div>
          </Toolbar>

          {filtered.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title={reunioes.length ? "Nenhuma reunião neste filtro" : "Nenhuma reunião registrada"}
              description={reunioes.length ? "Ajuste a busca ou os filtros." : "Agende a primeira e registre a ata depois."}
              action={!reunioes.length && <Button size="sm" className="gap-1.5" onClick={openNew}><Plus className="h-4 w-4" />Nova reunião</Button>}
            />
          ) : (
            <>
              <DataTable minWidth={820}>
                <thead>
                  <tr className="border-b border-border">
                    <th className={th}>Reunião</th>
                    <th className={th}>Quando</th>
                    <th className={cn(th, "text-right")}>Participantes</th>
                    <th className={th}>Status</th>
                    <th className={th}>Ata</th>
                    <th className={cn(th, "w-px")}><span className="sr-only">Ações</span></th>
                  </tr>
                </thead>
                <tbody>
                  {pager.slice.map(r => {
                    const TipoIcon = TIPO_ICONS[r.tipo]
                    return (
                      <tr key={r.id} className={rowClass} onClick={() => openEdit(r)}>
                        <td className={td}>
                          <div className="flex items-center gap-3">
                            <IconTile icon={TipoIcon} />
                            <div className="min-w-0">
                              <div className="max-w-[300px] truncate text-[13px] font-medium text-foreground">{r.titulo}</div>
                              <div className="text-[12px] text-muted-foreground">{TIPO_LABELS[r.tipo]}{r.local ? ` · ${r.local}` : ""}</div>
                            </div>
                          </div>
                        </td>
                        <td className={td}>
                          <div className="text-[13px] tabular-nums text-foreground">{fmtDate(r.data)}</div>
                          <div className="text-[12px] tabular-nums text-muted-foreground">{fmtHora(r.horaInicio)}{r.horaFim ? ` – ${r.horaFim}` : ""}</div>
                        </td>
                        <td className={cn(td, "text-right text-[13px] tabular-nums text-foreground")}>{r.participantes.length}</td>
                        <td className={td}><Tag tone={STATUS_TONE[r.status]} dot>{STATUS_LABELS[r.status]}</Tag></td>
                        <td className={td}>{r.ata ? <Tag tone="success">Registrada</Tag> : <span className="text-[12px] text-muted-foreground">Pendente</span>}</td>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onSelect={() => openEdit(r)}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => openAta(r)}><ClipboardList className="mr-2 h-4 w-4" />{r.ata ? "Ver ata" : "Registrar ata"}</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onSelect={() => setDeletingId(r.id)} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"><Trash2 className="mr-2 h-4 w-4" />Excluir</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </DataTable>
              <Pagination {...pager} noun="reuniões" />
            </>
          )}
        </Panel>
      </Page>

      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir reunião?</AlertDialogTitle>
            <AlertDialogDescription>A reunião e a ata registrada serão removidas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => deletingId && handleDelete(deletingId)} className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Editor de Reunião */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Reunião" : "Nova Reunião"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input value={form.titulo} onChange={e => setField("titulo", e.target.value)} placeholder="Ex: Apresentação de proposta — Edifício Central" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={v => setField("tipo", v as TipoReuniao)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interna">Interna</SelectItem>
                    <SelectItem value="cliente">Com cliente</SelectItem>
                    <SelectItem value="tecnica">Técnica</SelectItem>
                    <SelectItem value="diretoria">Diretoria</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setField("status", v as StatusReuniao)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agendada">Agendada</SelectItem>
                    <SelectItem value="realizada">Realizada</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={form.data} onChange={e => setField("data", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Início</Label>
                <Input type="time" value={form.horaInicio} onChange={e => setField("horaInicio", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Término</Label>
                <Input type="time" value={form.horaFim ?? ""} onChange={e => setField("horaFim", e.target.value)} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Local</Label>
                <Input value={form.local ?? ""} onChange={e => setField("local", e.target.value)} placeholder="Ex: Sala de reuniões" className="mt-1" />
              </div>
              <div>
                <Label>Link (Zoom/Meet)</Label>
                <Input value={form.linkVideo ?? ""} onChange={e => setField("linkVideo", e.target.value)} placeholder="https://..." className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Participantes</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={novoParticipante}
                  onChange={e => setNovoParticipante(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addParticipante()}
                  placeholder="Nome do participante (Enter para adicionar)"
                />
                <Button type="button" size="sm" variant="outline" onClick={addParticipante}><Plus className="h-4 w-4" /></Button>
              </div>
              {form.participantes.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.participantes.map(p => (
                    <span key={p.id} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full">
                      {p.nome}
                      <button onClick={() => removeParticipante(p.id)} className="text-gray-400 hover:text-red-500 ml-0.5">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea value={form.observacoes ?? ""} onChange={e => setField("observacoes", e.target.value)} placeholder="Observações gerais..." className="mt-1 min-h-[60px]" />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Salvar reunião</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ata Modal */}
      <AtaModal open={ataOpen} reuniao={reuniaoAta} onClose={() => setAtaOpen(false)} onSave={handleSaveAta} />
    </SuperAdminLayout>
  )
}

export default ReunioesComercias
